/**
 * Video.jsx
 * Making a video with P-Video on Replicate.
 *
 * Laid out as Replicate's own playground is — the inputs on the left, the
 * result on the right — with the same inputs, limits and defaults, so a prompt
 * that works there works here. Recent videos sit underneath.
 *
 * A finished video is library media. It goes out through Create like any
 * uploaded video, so nothing on this screen posts anything.
 *
 * Dependencies: services/api.js, hooks/useBrands.jsx, components/video/*, utils/video.js
 */

import { useCallback, useEffect, useState } from 'react';
import { useBrands, useSelectedBrand } from '../hooks/useBrands.jsx';
import { api } from '../services/api.js';
import VideoForm from '../components/video/VideoForm.jsx';
import VideoOutput from '../components/video/VideoOutput.jsx';
import VideoHistory from '../components/video/VideoHistory.jsx';
import { Warning } from '../components/Icons.jsx';
import { ACTIVE, bodyFromForm, emptyForm, formFromJob } from '../utils/video.js';

/** Replicate makes a 5-second clip in about ten seconds, so a few seconds is right. */
const POLL_MS = 3000;

export default function Video() {
  const { brands, loading } = useBrands();
  const [brandId, setBrandId] = useSelectedBrand();

  const [options, setOptions] = useState(null);
  const [form, setForm] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [checkError, setCheckError] = useState(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);

  const loadOptions = useCallback(
    () =>
      api.videos
        .options()
        .then((o) => {
          setOptions(o);
          setForm((f) => f ?? emptyForm(o));
        })
        .catch(setError),
    [],
  );

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  const loadJobs = useCallback(async () => {
    if (!brandId) return;
    try {
      const { videos } = await api.videos.list(brandId);
      setJobs(videos);
    } catch (e) {
      setError(e);
    }
  }, [brandId]);

  useEffect(() => {
    setCurrentId(null);
    loadJobs();
  }, [loadJobs]);

  // Every video still being made is asked about while the screen is open.
  // Asking is also what saves a finished one, so closing the tab only means
  // the every-minute cron does it instead.
  const activeIds = jobs
    .filter((j) => ACTIVE.includes(j.status))
    .map((j) => j.id)
    .join(',');

  useEffect(() => {
    if (!activeIds) return undefined;
    let stopped = false;

    const tick = async () => {
      for (const id of activeIds.split(',')) {
        try {
          const { video, checkError: problem } = await api.videos.get(id);
          if (stopped) return;
          setJobs((list) => list.map((j) => (j.id === video.id ? video : j)));
          if (id === currentId) setCheckError(problem ?? null);
          // A saved video has moved the month's spend.
          if (video.status === 'saved') loadOptions();
        } catch {
          /* asked again on the next tick */
        }
      }
    };

    tick();
    const timer = setInterval(tick, POLL_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [activeIds, currentId, loadOptions]);

  async function run() {
    setStarting(true);
    setError(null);
    try {
      const { video } = await api.videos.start(bodyFromForm(form, brandId));
      setJobs((list) => [video, ...list]);
      setCurrentId(video.id);
      setCheckError(null);
      loadOptions();
    } catch (e) {
      setError(e);
    } finally {
      setStarting(false);
    }
  }

  async function cancel(job) {
    try {
      const { video } = await api.videos.cancel(job.id);
      setJobs((list) => list.map((j) => (j.id === video.id ? video : j)));
    } catch (e) {
      setError(e);
    }
  }

  function tweak(job) {
    setForm(formFromJob(job, options));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const current = jobs.find((j) => j.id === currentId) ?? null;

  if (!loading && brands.length === 0) {
    return (
      <div className="notice notice--info">
        <Warning />
        <span>Add a brand first — videos are kept in a brand’s library.</span>
      </div>
    );
  }

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-head__eyebrow">Video</div>
          <h1>Make a video</h1>
          <p>
            {options?.label ?? 'P-Video'} on Replicate: text, image or audio to video. Finished
            videos go straight into the library, ready to use in a post.
          </p>
        </div>

        {brands.length > 1 && (
          <div className="page-head__actions">
            <select value={brandId} onChange={(e) => setBrandId(e.target.value)} aria-label="Brand">
              {brands.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {options && !options.configured && (
        <div className="notice notice--error">
          <Warning />
          <span>
            Replicate isn’t connected yet. Add your Replicate API token as the{' '}
            <code>REPLICATE_API_TOKEN</code> Worker secret, then deploy.
          </span>
        </div>
      )}

      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}

      {!options || !form ? (
        <p className="mono">Loading…</p>
      ) : (
        <div className="vid">
          <section className="card vid__panel">
            <VideoForm
              brandId={brandId}
              options={options}
              form={form}
              onChange={setForm}
              onReset={() => setForm(emptyForm(options))}
              onRun={run}
              running={starting}
            />
          </section>

          <section className="card vid__panel vid__panel--out">
            <VideoOutput job={current} checkError={checkError} onCancel={cancel} onTweak={tweak} />
          </section>
        </div>
      )}

      <VideoHistory jobs={jobs} currentId={currentId} onOpen={setCurrentId} />
    </>
  );
}
